/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Row, Select, TablePaginationConfig } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { MailOutlined, ReloadOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import styled from "styled-components";
import UITransition from "../../ui/Transition";
import { UITitle } from "../../ui/Title";
import { UIText } from "../../ui/Text";
import { UICard } from "../../ui/Card";
import { UITable } from "../../ui/Table";
import { UIButton } from "../../ui/Button";
import { UIAlert } from "../../ui/Alert";
import { UISearch } from "../../ui/SearchInput";
import { UIRangePicker } from "../../ui/RangePicker";
import { UISelect } from "../../ui/Select";
import { BaseColor, Colors } from "../../../theme/maps/Colors";
import { showNotification } from "../../../utils/showNotification";
import {
  apiIntermittentAction,
  apiIntermittentList,
  apiIntermittentSummary,
  intermittentErrorMessage,
} from "../../../api/apiIntermittentCommunications";
import {
  TIntermittentAction,
  TIntermittentCommunication,
  TIntermittentFilters,
  TIntermittentList,
  TIntermittentStatus,
  TIntermittentSummary,
  TIntermittentWorkerType,
} from "../../../redux-modules/declarations/maps/intermittentCommunications";
import {
  ACTION_META,
  fmtDay,
  fmtShift,
  fullName,
  isOvernight,
  nextStep,
  STATUS_META,
  STATUS_ORDER,
  TNextStepTone,
} from "./constants";
import { StatusTag } from "./partials/StatusTag";
import { SummaryCards, TQuickView } from "./partials/SummaryCards";
import { DetailDrawer } from "./partials/DetailDrawer";
import { ActionModal } from "./partials/ActionModal";
import { WorkerTypeTags } from "./partials/WorkerTypeTags";

/** Evento per aggiornare il badge nel menu laterale dopo un'azione. */
export const INTERMITTENT_REFRESH_EVENT = "intermittent-communications:refresh";

const QUICK_VIEW_STATUSES: Record<Exclude<TQuickView, "all" | "needs_action">, TIntermittentStatus[]> = {
  scheduled: ["scheduled"],
  sent: ["sent"],
  cancellation_pending: ["cancellation_pending"],
};

const PAGE_SIZE = 20;

const QUICK_VIEW_LABELS: Record<Exclude<TQuickView, "all">, { title: string; hint: string }> = {
  needs_action: {
    title: "da gestire",
    hint: "Le comunicazioni in programma, inviate o chiuse non sono in elenco.",
  },
  scheduled: { title: "in programma", hint: "Partiranno in automatico all'orario indicato." },
  sent: { title: "inviate", hint: "In attesa del controllo presenze dopo il turno." },
  cancellation_pending: {
    title: "in attesa di annullamento",
    hint: "Se il lavoratore era presente, intervieni prima dell'annullamento automatico.",
  },
};

export const ViewIntermittentCommunications = (): JSX.Element => {
  const [summary, setSummary] = useState<TIntermittentSummary>();
  const [list, setList] = useState<TIntermittentList>();
  const [loading, setLoading] = useState(false);

  const [quickView, setQuickView] = useState<TQuickView>("all");
  const [statuses, setStatuses] = useState<TIntermittentStatus[]>([]);
  const [workerType, setWorkerType] = useState<TIntermittentWorkerType>();
  const [range, setRange] = useState<[Moment, Moment] | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const [openId, setOpenId] = useState<number>();
  const [reloadKey, setReloadKey] = useState(0);
  const [pending, setPending] = useState<{ communication: TIntermittentCommunication; action: TIntermittentAction }>();
  const [actionLoading, setActionLoading] = useState(false);
  const initialized = useRef(false);

  const loadSummary = useCallback(async (): Promise<TIntermittentSummary | undefined> => {
    try {
      const s = await apiIntermittentSummary();
      setSummary(s);
      return s;
    } catch (err) {
      showNotification("error", "Comunicazioni", intermittentErrorMessage(err, "Impossibile caricare il riepilogo."));
    }
    return undefined;
  }, []);

  const loadList = useCallback(async (): Promise<void> => {
    const filters: TIntermittentFilters = {
      page,
      per_page: PAGE_SIZE,
      sort,
      search: search.trim() || undefined,
      worker_type: workerType,
      date_from: range ? range[0].format("YYYY-MM-DD") : undefined,
      date_to: range ? range[1].format("YYYY-MM-DD") : undefined,
    };
    if (quickView === "needs_action") filters.needs_action = true;
    else if (quickView !== "all") filters.status = QUICK_VIEW_STATUSES[quickView];
    else if (statuses.length) filters.status = statuses;

    setLoading(true);
    try {
      setList(await apiIntermittentList(filters));
    } catch (err) {
      showNotification("error", "Comunicazioni", intermittentErrorMessage(err, "Impossibile caricare le comunicazioni."));
    } finally {
      setLoading(false);
    }
  }, [page, sort, search, range, quickView, statuses, workerType]);

  // Primo caricamento: se ci sono comunicazioni da gestire, si parte da quelle
  useEffect(() => {
    loadSummary().then(s => {
      if (s && s.needs_action > 0) setQuickView("needs_action");
      initialized.current = true;
      setReloadKey(k => k + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialized.current) loadList();
  }, [loadList, reloadKey]);

  const refreshAll = (): void => {
    loadSummary();
    setReloadKey(k => k + 1);
    window.dispatchEvent(new Event(INTERMITTENT_REFRESH_EVENT));
  };

  const selectQuickView = (view: TQuickView): void => {
    setQuickView(view);
    setStatuses([]);
    setPage(1);
  };

  const resetFilters = (): void => {
    setQuickView("all");
    setStatuses([]);
    setRange(null);
    setWorkerType(undefined);
    setSearch("");
    setSearchInput("");
    setPage(1);
  };

  const runAction = async (note?: string): Promise<void> => {
    if (!pending) return;
    setActionLoading(true);
    try {
      const updated = await apiIntermittentAction(pending.communication.id, pending.action, note);
      showNotification(
        "success",
        ACTION_META[pending.action].label,
        `${fullName(updated)} – ${fmtDay(updated.work_date)}: ${STATUS_META[updated.status]?.label || updated.status_label}`,
      );
      setPending(undefined);
      refreshAll();
    } catch (err) {
      showNotification("error", ACTION_META[pending.action].label, intermittentErrorMessage(err, "Operazione non riuscita."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<TIntermittentCommunication> | SorterResult<TIntermittentCommunication>[],
  ): void => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSort = s && s.order === "ascend" ? "asc" : "desc";
    if (nextSort !== sort) {
      setSort(nextSort);
      setPage(1);
    } else {
      setPage(pagination.current || 1);
    }
  };

  const hasFilters = quickView !== "all" || statuses.length > 0 || !!range || !!search || !!workerType;

  const columns = [
    {
      title: "Dipendente",
      key: "employee",
      showMobile: true,
      width: 250,
      // eslint-disable-next-line react/display-name
      render: (_: unknown, c: TIntermittentCommunication): JSX.Element => (
        <div>
          <UIText strong>{fullName(c)}</UIText>
          <div style={{ marginTop: 2, whiteSpace: "nowrap" }}>
            <WorkerTypeTags types={c.worker_types} small />
            <UIText type="secondary" style={{ fontSize: 12 }}>
              {c.fiscal_code || "Codice fiscale mancante"}
            </UIText>
          </div>
        </div>
      ),
    },
    {
      title: "Giorno e turno",
      dataIndex: "work_date",
      key: "work_date",
      showMobile: true,
      sorter: true,
      sortOrder: sort === "asc" ? ("ascend" as const) : ("descend" as const),
      sortDirections: ["descend" as const, "ascend" as const, "descend" as const],
      width: 165,
      // eslint-disable-next-line react/display-name
      render: (d: string, c: TIntermittentCommunication): JSX.Element => (
        <div>
          <NoWrap>{fmtDay(d)}</NoWrap>
          <div>
            <UIText type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              {fmtShift(c)}
              {isOvernight(c) ? " (+1)" : ""}
            </UIText>
          </div>
        </div>
      ),
    },
    {
      title: "Sede",
      key: "office",
      width: 140,
      // eslint-disable-next-line react/display-name
      render: (_: unknown, c: TIntermittentCommunication): JSX.Element => (
        <span>{c.offices && c.offices.length ? c.offices.join(", ") : "-"}</span>
      ),
    },
    {
      title: "Stato",
      dataIndex: "status",
      key: "status",
      showMobile: true,
      width: 195,
      // eslint-disable-next-line react/display-name
      render: (s: TIntermittentStatus, c: TIntermittentCommunication): JSX.Element => (
        <StatusTag status={s} label={c.status_label} />
      ),
    },
    {
      title: "Prossimo passo",
      key: "next",
      // eslint-disable-next-line react/display-name
      render: (_: unknown, c: TIntermittentCommunication): JSX.Element => {
        const step = nextStep(c);
        return <NextStep tone={step.tone}>{step.text}</NextStep>;
      },
    },
    {
      title: "",
      key: "open",
      width: 100,
      align: "right" as const,
      // eslint-disable-next-line react/display-name
      render: (_: unknown, c: TIntermittentCommunication): JSX.Element => (
        <UIButton
          size="small"
          type={c.available_actions.length && STATUS_META[c.status]?.needsAction ? "primary" : "default"}
          onClick={(e): void => {
            e.stopPropagation();
            setOpenId(c.id);
          }}
        >
          {c.available_actions.length && STATUS_META[c.status]?.needsAction ? "Gestisci" : "Dettaglio"}
        </UIButton>
      ),
    },
  ];

  return (
    <UITransition>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <UITitle level={2} color={BaseColor} data-cy="page-title" style={{ marginBottom: 0 }}>
            Comunicazioni Intermittenti
          </UITitle>
          <UIText type="secondary" style={{ display: "block" }}>
            Chiamate di intermittenti e occasionali:{" "}
            <UIText strong>invio automatico {summary?.send_before_hours ?? 25} ore prima del turno</UIText>, annullamento
            se manca la timbratura.
          </UIText>
          {summary && (
            <UIText type="secondary" style={{ display: "block" }} data-cy="intermittent-xml-recipients">
              <MailOutlined style={{ marginRight: 6 }} />
              Destinatario degli XML:{" "}
              {summary.xml_recipients.length ? (
                <UIText strong>{summary.xml_recipients.join(", ")}</UIText>
              ) : (
                <UIText type="danger">non configurato</UIText>
              )}
            </UIText>
          )}
        </Col>
        <Col>
          <UIButton icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>
            Aggiorna
          </UIButton>
        </Col>
      </Row>

      <div style={{ height: 16 }} />

      {summary && !summary.enabled && (
        <UIAlert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Invio automatico disattivato"
          description="Le comunicazioni non vengono pianificate né inviate in automatico. Le azioni manuali restano disponibili."
        />
      )}

      <SummaryCards summary={summary} active={quickView} onSelect={selectQuickView} />

      <UICard>
        <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} md={12} xl={6}>
            <UISearch
              allowClear
              placeholder="Cerca dipendente o codice fiscale"
              value={searchInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setSearchInput(e.target.value)}
              onSearch={(v: string): void => {
                setSearch(v);
                setPage(1);
              }}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <UIRangePicker
              format="DD/MM/YYYY"
              placeholder={["Dal giorno", "Al giorno"]}
              value={range}
              onChange={(v): void => {
                setRange(v && v[0] && v[1] ? [v[0], v[1]] : null);
                setPage(1);
              }}
              ranges={{
                Oggi: [moment(), moment()],
                "Prossimi 7 giorni": [moment(), moment().add(7, "days")],
                "Ultimi 7 giorni": [moment().subtract(7, "days"), moment()],
              }}
            />
          </Col>
          <Col xs={24} md={10} xl={5}>
            <UISelect
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder="Filtra per stato"
              style={{ width: "100%" }}
              value={statuses}
              disabled={quickView !== "all"}
              onChange={(v): void => {
                setStatuses(v as TIntermittentStatus[]);
                setPage(1);
              }}
            >
              {STATUS_ORDER.map(s => (
                <Select.Option key={s} value={s}>
                  {STATUS_META[s].label}
                </Select.Option>
              ))}
            </UISelect>
          </Col>
          <Col xs={24} md={9} xl={4}>
            <UISelect
              allowClear
              placeholder="Tipologia"
              style={{ width: "100%" }}
              value={workerType}
              onChange={(v): void => {
                setWorkerType((v as TIntermittentWorkerType) || undefined);
                setPage(1);
              }}
            >
              <Select.Option value="intermittente">Intermittenti</Select.Option>
              <Select.Option value="occasionale">Occasionali</Select.Option>
            </UISelect>
          </Col>
          <Col xs={24} md={5} xl={3} style={{ textAlign: "right" }}>
            <UIButton type="link" onClick={resetFilters} disabled={!hasFilters}>
              Azzera filtri
            </UIButton>
          </Col>
        </Row>

        {quickView !== "all" && (
          <UIAlert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <span>
                Stai vedendo solo: <strong>{QUICK_VIEW_LABELS[quickView].title}</strong>.{" "}
                {QUICK_VIEW_LABELS[quickView].hint}
              </span>
            }
            action={
              <UIButton size="small" onClick={(): void => selectQuickView("all")}>
                Mostra tutte
              </UIButton>
            }
          />
        )}

        <UITable
          size="middle"
          rowKey="id"
          data-cy="intermittent-communications-table"
          loading={loading}
          columns={columns}
          dataSource={list?.data}
          onChange={handleTableChange}
          onRow={(c: TIntermittentCommunication): React.HTMLAttributes<HTMLElement> => ({
            onClick: (): void => setOpenId(c.id),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: list?.pagination.current || page,
            pageSize: PAGE_SIZE,
            total: list?.pagination.total || 0,
            showSizeChanger: false,
            showTotal: (total: number): string => `${total} comunicazion${total === 1 ? "e" : "i"}`,
          }}
          locale={{
            emptyText:
              quickView === "needs_action"
                ? "Nessuna comunicazione da gestire"
                : "Nessuna comunicazione per i filtri selezionati",
          }}
        />
      </UICard>

      <DetailDrawer
        communicationId={openId}
        reloadKey={reloadKey}
        onClose={(): void => setOpenId(undefined)}
        onAction={(communication, action): void => setPending({ communication, action })}
        onUpdated={refreshAll}
        xmlRecipients={summary?.xml_recipients}
      />

      <ActionModal
        communication={pending?.communication}
        action={pending?.action}
        loading={actionLoading}
        onCancel={(): void => setPending(undefined)}
        onConfirm={runAction}
      />
    </UITransition>
  );
};

const NoWrap = styled.span`
  white-space: nowrap;
`;

const NextStep = styled.span<{ tone: TNextStepTone }>`
  color: ${(p): string =>
    p.tone === "danger"
      ? Colors.red
      : p.tone === "warning"
      ? "#d46b08"
      : p.tone === "secondary"
      ? "rgba(0, 0, 0, 0.45)"
      : "rgba(0, 0, 0, 0.85)"};
`;
